import React, { useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  DocumentEditorContainerComponent,
  Toolbar,
  SpellChecker,
  Inject,
} from '@syncfusion/ej2-react-documenteditor';
import { getApiBaseUrl } from '../utils/apiUrl';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';

// Syncfusion CSS importieren
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-react-documenteditor/styles/material.css';

// Syncfusion Lizenz registrieren (Community-Lizenz oder Trial)
import { registerLicense } from '@syncfusion/ej2-base';

// Deutsche Lokalisierung importieren und initialisieren
import { initSyncfusionLocale } from '../utils/syncfusionLocale';

// WICHTIG: Ersetzen Sie dies mit Ihrem Syncfusion-Lizenzschlüssel
// Kostenlose Community-Lizenz: https://www.syncfusion.com/products/communitylicense
const SYNCFUSION_LICENSE_KEY = process.env.REACT_APP_SYNCFUSION_LICENSE_KEY || '';
if (SYNCFUSION_LICENSE_KEY) {
  registerLicense(SYNCFUSION_LICENSE_KEY);
}

// Deutsche Lokalisierung initialisieren
initSyncfusionLocale();

interface SyncfusionEditorProps {
  documentId?: number;
  vorlageId?: number; // Für Vorlagen-Bearbeitung
  documentUrl?: string;
  height?: string;
  onSave?: (blob: Blob) => void;
  onContentChange?: () => void;
  onSendEmail?: (blob: Blob) => void; // Callback für E-Mail senden
}

export interface SyncfusionEditorHandle {
  insertText: (text: string) => void;
  insertHtml: (html: string) => void;
  insertContent: (html: string) => void;
  getHTML: () => string;
  getDocumentAsBlob: () => Promise<Blob | null>;
  openDocument: (url: string) => Promise<void>;
  saveDocument: () => void;
  getSelectedText: () => string;
  replaceSelection: (text: string) => void;
  getDocumentText: () => string;
  print: () => void;
}

const SyncfusionEditor = forwardRef<SyncfusionEditorHandle, SyncfusionEditorProps>(
  ({ documentId, vorlageId, documentUrl, height = '600px', onSave, onContentChange, onSendEmail }, ref) => {
    const containerRef = useRef<DocumentEditorContainerComponent>(null);
    const [isEditorReady, setIsEditorReady] = React.useState(false);
    const [showRuler, setShowRuler] = React.useState(true);

    // Editor-Methoden nach außen exponieren
    useImperativeHandle(ref, () => ({
      // Text an Cursor-Position einfügen
      insertText: (text: string) => {
        if (containerRef.current?.documentEditor) {
          // Editor fokussieren vor dem Einfügen
          containerRef.current.documentEditor.focusIn();
          // Kurze Verzögerung um sicherzustellen dass Focus wirksam ist
          setTimeout(() => {
            containerRef.current?.documentEditor?.editor.insertText(text);
          }, 10);
        }
      },

      // HTML-formatierten Text einfügen (wird als SFDT konvertiert)
      insertHtml: (html: string) => {
        if (containerRef.current?.documentEditor) {
          // Editor fokussieren
          containerRef.current.documentEditor.focusIn();
          // Syncfusion unterstützt direktes HTML-Einfügen nicht nativ
          // Wir fügen den Text-Inhalt ein
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const plainText = tempDiv.textContent || tempDiv.innerText || '';
          setTimeout(() => {
            containerRef.current?.documentEditor?.editor.insertText(plainText);
          }, 10);
        }
      },

      // Textbaustein einfügen (Alias für insertHtml, für Kompatibilität mit anderen Editoren)
      insertContent: (html: string) => {
        if (containerRef.current?.documentEditor) {
          // Editor fokussieren
          containerRef.current.documentEditor.focusIn();

          // Prüfen ob HTML ein Bild enthält
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const imgElement = tempDiv.querySelector('img');

          setTimeout(() => {
            if (!containerRef.current?.documentEditor) return;

            if (imgElement && imgElement.src) {
              // Bild einfügen
              const imgSrc = imgElement.src;
              // Breite und Höhe aus style oder Attributen extrahieren
              let width = 300; // Default
              let height = 100; // Default

              if (imgElement.style.maxWidth) {
                const maxWidth = parseInt(imgElement.style.maxWidth);
                if (!isNaN(maxWidth)) width = maxWidth;
              }
              if (imgElement.style.maxHeight) {
                const maxHeight = parseInt(imgElement.style.maxHeight);
                if (!isNaN(maxHeight)) height = maxHeight;
              }
              if (imgElement.width) width = imgElement.width;
              if (imgElement.height) height = imgElement.height;

              console.log('SyncfusionEditor: Füge Bild ein', { width, height, srcLength: imgSrc.length });
              containerRef.current.documentEditor.editor.insertImage(imgSrc, width, height);
            } else {
              // Nur Text einfügen
              const plainText = tempDiv.textContent || tempDiv.innerText || '';
              containerRef.current.documentEditor.editor.insertText(plainText);
            }
          }, 10);
        }
      },

      // Dokument-Inhalt als Text zurückgeben (Syncfusion hat kein direktes HTML-Export)
      getHTML: (): string => {
        if (containerRef.current?.documentEditor) {
          // Syncfusion exportiert SFDT, nicht HTML - wir geben leeren String zurück
          // Der tatsächliche Inhalt wird als DOCX-Blob gespeichert
          return '';
        }
        return '';
      },

      // Dokument als Blob exportieren
      getDocumentAsBlob: async (): Promise<Blob | null> => {
        if (containerRef.current?.documentEditor) {
          return new Promise((resolve) => {
            containerRef.current!.documentEditor.saveAsBlob('Docx').then((blob: Blob) => {
              resolve(blob);
            }).catch(() => {
              resolve(null);
            });
          });
        }
        return null;
      },

      // Dokument von URL öffnen
      openDocument: async (url: string) => {
        if (containerRef.current?.documentEditor) {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onload = () => {
              // Für DOCX-Dateien muss man den Syncfusion Web API Service nutzen
              // oder die Datei direkt laden
              containerRef.current?.documentEditor.open(reader.result as string);
            };
            reader.readAsDataURL(blob);
          } catch (error) {
            console.error('Fehler beim Öffnen des Dokuments:', error);
          }
        }
      },

      // Dokument speichern
      saveDocument: () => {
        if (containerRef.current?.documentEditor && onSave) {
          containerRef.current.documentEditor.saveAsBlob('Docx').then((blob: Blob) => {
            onSave(blob);
          });
        }
      },

      // Ausgewählten Text abrufen
      getSelectedText: (): string => {
        if (containerRef.current?.documentEditor?.selection) {
          return containerRef.current.documentEditor.selection.text || '';
        }
        return '';
      },

      // Auswahl durch neuen Text ersetzen (oder an Cursor-Position einfügen)
      replaceSelection: (text: string) => {
        if (containerRef.current?.documentEditor?.editor) {
          // Editor fokussieren
          containerRef.current.documentEditor.focusIn();
          // insertText ersetzt automatisch die aktuelle Auswahl oder fügt an Cursor-Position ein
          setTimeout(() => {
            containerRef.current?.documentEditor?.editor.insertText(text);
          }, 10);
        }
      },

      // Gesamten Dokumenttext abrufen (für KI-Verbesserung des ganzen Dokuments)
      getDocumentText: (): string => {
        if (containerRef.current?.documentEditor) {
          try {
            // Alle Inhalte selektieren, Text kopieren, dann Auswahl aufheben
            const editor = containerRef.current.documentEditor;
            const selection = editor.selection;

            // Aktuelle Cursor-Position merken
            const currentStart = selection.startOffset;
            const currentEnd = selection.endOffset;

            // Alles auswählen
            editor.selection.selectAll();
            const fullText = selection.text || '';

            // Auswahl aufheben und zur ursprünglichen Position zurückkehren
            selection.select(currentStart, currentEnd);

            return fullText;
          } catch (e) {
            console.error('Fehler beim Abrufen des Dokumenttexts:', e);
            return '';
          }
        }
        return '';
      },

      // Dokument drucken
      print: () => {
        if (containerRef.current?.documentEditor) {
          containerRef.current.documentEditor.print();
        }
      },
    }));

    // Dokument laden wenn documentId oder vorlageId angegeben ist
    useEffect(() => {
      const loadDocument = async () => {
        if (!isEditorReady || !containerRef.current?.documentEditor) {
          console.log('SyncfusionEditor: Editor noch nicht bereit');
          return;
        }

        const apiUrl = getApiBaseUrl();

        // Vorlagen-Dokument laden (hat Priorität)
        if (vorlageId) {
          try {
            console.log('SyncfusionEditor: Lade Vorlage mit ID', vorlageId);
            const response = await fetch(`${apiUrl}/api/syncfusion/ImportVorlage/${vorlageId}`);

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({ message: response.statusText }));
              throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            const sfdt = await response.text();
            console.log('SyncfusionEditor: Vorlage SFDT geladen, Länge:', sfdt.length);

            // SFDT in Editor laden
            containerRef.current.documentEditor.open(sfdt);
            console.log('SyncfusionEditor: Vorlage erfolgreich geladen');
          } catch (error: any) {
            console.error('SyncfusionEditor: Fehler beim Laden der Vorlage:', error);
            alert('Fehler beim Laden der Vorlage: ' + (error.message || 'Unbekannter Fehler'));
          }
        } else if (documentId) {
          try {
            console.log('SyncfusionEditor: Lade Dokument mit ID', documentId);
            const response = await fetch(`${apiUrl}/api/syncfusion/ImportDocument/${documentId}`);

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const sfdt = await response.text();
            console.log('SyncfusionEditor: SFDT geladen, Länge:', sfdt.length);

            // SFDT in Editor laden
            containerRef.current.documentEditor.open(sfdt);
            console.log('SyncfusionEditor: Dokument erfolgreich geladen');
          } catch (error) {
            console.error('SyncfusionEditor: Fehler beim Laden des Dokuments:', error);
          }
        } else if (documentUrl) {
          // Fallback: URL-basiertes Laden
          try {
            console.log('SyncfusionEditor: Lade Dokument von URL', documentUrl);
            const response = await fetch(documentUrl);
            const blob = await response.blob();

            // Dokument über Import-Endpoint konvertieren
            const formData = new FormData();
            formData.append('file', blob, 'document.docx');

            const importResponse = await fetch(`${apiUrl}/api/syncfusion/Import`, {
              method: 'POST',
              body: formData,
            });

            if (!importResponse.ok) {
              throw new Error(`Import fehlgeschlagen: ${importResponse.status}`);
            }

            const sfdt = await importResponse.text();
            containerRef.current.documentEditor.open(sfdt);
            console.log('SyncfusionEditor: Dokument von URL erfolgreich geladen');
          } catch (error) {
            console.error('SyncfusionEditor: Fehler beim Laden des Dokuments von URL:', error);
          }
        }
      };

      loadDocument();
    }, [documentId, vorlageId, documentUrl, isEditorReady]);

    // Lineal ein/ausschalten
    const toggleRuler = () => {
      if (containerRef.current?.documentEditor) {
        const newShowRuler = !showRuler;
        containerRef.current.documentEditor.documentEditorSettings.showRuler = newShowRuler;
        setShowRuler(newShowRuler);
      }
    };

    // Editor bereit-Callback
    const handleCreated = () => {
      console.log('SyncfusionEditor: Editor ist bereit');
      setIsEditorReady(true);
      // Lineal initial aktivieren
      if (containerRef.current?.documentEditor) {
        containerRef.current.documentEditor.documentEditorSettings.showRuler = true;

        // SpellCheck aktivieren und konfigurieren
        const spellChecker = containerRef.current.documentEditor.spellChecker;
        if (spellChecker) {
          const apiUrl = getApiBaseUrl();
          spellChecker.languageID = 1031; // Deutsch
          spellChecker.removeUnderline = false;
          spellChecker.allowSpellCheckAndSuggestion = true;
          // Setze die SpellCheck URL auf unseren Backend-Endpoint
          // Syncfusion erwartet die Basis-URL und fügt selbst /SpellCheck oder /SpellCheckByPage hinzu
          // Type assertion notwendig, da TypeScript-Definitionen nicht alle Properties enthalten
          (spellChecker as any).spellingServiceUrl = `${apiUrl}/api/syncfusion/`;
          console.log('SyncfusionEditor: SpellCheck konfiguriert für Deutsch (1031)', (spellChecker as any).spellingServiceUrl);
        }
      }
    };

    // Drucken-Funktion
    const handlePrint = () => {
      if (containerRef.current?.documentEditor) {
        containerRef.current.documentEditor.print();
      }
    };

    // E-Mail senden-Funktion
    const handleSendEmail = async () => {
      if (containerRef.current?.documentEditor) {
        try {
          const blob = await containerRef.current.documentEditor.saveAsBlob('Docx');
          if (onSendEmail) {
            onSendEmail(blob);
          } else {
            // Fallback: Öffne E-Mail-Dialog mit Dokument-Download
            alert('E-Mail-Funktion: Bitte speichern Sie das Dokument zuerst und verwenden Sie dann die E-Mail-Funktion in der Dokumentübersicht.');
          }
        } catch (error) {
          console.error('Fehler beim Vorbereiten des Dokuments für E-Mail:', error);
          alert('Fehler beim Vorbereiten des Dokuments für E-Mail');
        }
      }
    };

    return (
      <div style={{ height, display: 'flex', flexDirection: 'column' }}>
        {/* Custom Toolbar mit Drucken, E-Mail und Lineal */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 8px',
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #ddd'
        }}>
          {/* Linke Seite: Drucken und E-Mail */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handlePrint}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                cursor: 'pointer',
                backgroundColor: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 500,
                transition: 'background-color 0.2s'
              }}
              title="Dokument drucken"
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
            >
              <PrintIcon style={{ fontSize: '18px' }} />
              Drucken
            </button>
            <button
              onClick={handleSendEmail}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                cursor: 'pointer',
                backgroundColor: '#2e7d32',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 500,
                transition: 'background-color 0.2s'
              }}
              title="Dokument per E-Mail senden"
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1b5e20'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2e7d32'}
            >
              <EmailIcon style={{ fontSize: '18px' }} />
              E-Mail senden
            </button>
          </div>

          {/* Rechte Seite: Lineal Toggle */}
          <button
            onClick={toggleRuler}
            style={{
              padding: '4px 12px',
              fontSize: '12px',
              cursor: 'pointer',
              backgroundColor: showRuler ? '#1976d2' : '#e0e0e0',
              color: showRuler ? '#fff' : '#333',
              border: '1px solid #ccc',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title={showRuler ? 'Lineal ausblenden' : 'Lineal einblenden'}
          >
            <span style={{ fontSize: '14px' }}>📏</span>
            Lineal {showRuler ? 'aus' : 'ein'}
          </button>
        </div>
        <DocumentEditorContainerComponent
          ref={containerRef}
          height="100%"
          enableToolbar={true}
          enableSpellCheck={true}
          enableComment={false}
          showPropertiesPane={true}
          locale="de"
          serviceUrl={`${getApiBaseUrl()}/api/syncfusion/`}
          documentEditorSettings={{
            showRuler: true
          }}
          contentChange={() => {
            if (onContentChange) {
              onContentChange();
            }
          }}
          created={handleCreated}
        >
          <Inject services={[Toolbar, SpellChecker]} />
        </DocumentEditorContainerComponent>
      </div>
    );
  }
);

SyncfusionEditor.displayName = 'SyncfusionEditor';

export default SyncfusionEditor;
